import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { Card } from '../components/ui/Card';
import { format } from 'date-fns';
import { CheckCircle, AlertCircle, Clock, ChevronRight } from 'lucide-react';

type ZoneDMTransaction = {
  id: string;
  location_id: string;
  type: 'appointment' | 'new_patient' | 'patient';
  payload: any;
  status: 'received' | 'processing' | 'completed' | 'error';
  error?: string;
  created_at: string;
  processed_at?: string;
};

export default function ZoneDMTransactions() {
  const [selectedTransaction, setSelectedTransaction] = React.useState<ZoneDMTransaction | null>(null);

  const { data: transactions, isLoading } = useQuery({
    queryKey: ['zonedm-transactions'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      // Get user's integrations to understand their mapping
      const { data: integrations, error: integrationsError } = await supabase
        .from('integrations')
        .select('*')
        .eq('user_id', session.user.id);

      if (integrationsError) throw integrationsError;

      // Extract clientIds and locationIds from user's integrations
      const clientIds = integrations
        ?.map((integration: any) => integration.config?.clientId)
        .filter((id: any) => id !== null && id !== undefined);

      const locationIds = integrations
        ?.map((integration: any) => integration.config?.locationId)
        .filter((id: any) => id !== null && id !== undefined);

      console.log('User client IDs:', clientIds);
      console.log('User location IDs:', locationIds);

      // First try to get records from zonedm_transactions table
      const { data: zonedmTransactions, error: zonedmError } = await supabase
        .from('zonedm_transactions')
        .select('*')
        .order('created_at', { ascending: false });

      // Also get webhook_logs for comparison and debugging
      const { data: webhookLogs, error: webhookError } = await supabase
        .from('webhook_logs')
        .select('*')
        .order('created_at', { ascending: false });

      console.log('ZoneDM transactions count:', zonedmTransactions?.length || 0);
      console.log('Webhook logs count:', webhookLogs?.length || 0);

      if (zonedmError) {
        console.error('Error fetching zonedm_transactions:', zonedmError);
      }
      if (webhookError) {
        console.error('Error fetching webhook_logs:', webhookError);
      }

      // If we have ZoneDM transactions, use those
      if (zonedmTransactions && zonedmTransactions.length > 0) {
        return zonedmTransactions.map((tx: any) => ({
          ...tx,
          location_id: tx.location_id // This should have the actual location_id
        })) as ZoneDMTransaction[];
      }

      // Otherwise, fall back to webhook_logs but add location_id extraction
      const formattedWebhookLogs = (webhookLogs || []).map((log: any) => ({
        id: log.id,
        user_id: null,
        location_id: log.payload?.ClientId?.toString() || '', // Extract ClientId as location_id for display
        type: log.type,
        payload: log.payload,
        status: log.status,
        error: log.error,
        created_at: log.created_at,
        processed_at: log.processed_at
      }));

      // Filter webhook logs to show only those with matching ClientIds
      const filteredLogs = formattedWebhookLogs.filter((log: any) => {
        const clientId = log.payload?.ClientId;
        return clientId && clientIds?.includes(clientId.toString());
      });

      console.log('Filtered webhook logs count:', filteredLogs.length);
      return filteredLogs as ZoneDMTransaction[];
    },
  });

  const getStatusIcon = (status: ZoneDMTransaction['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'processing':
        return <Clock className="w-5 h-5 text-blue-500" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'received':
        return <Clock className="w-5 h-5 text-yellow-500" />;
    }
  };

  const getStatusClass = (status: ZoneDMTransaction['status']) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'error':
        return 'bg-red-100 text-red-800';
      case 'received':
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    return format(new Date(dateString), 'MMM d, yyyy HH:mm:ss');
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">ZoneDM Transactions</h1>
          <p className="mt-1 text-sm text-gray-500">
            Monitor and debug ZoneDM webhook transactions
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Transactions List */}
        <div className="lg:col-span-2">
          <Card>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Location ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Processed
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {transactions?.map((transaction) => (
                    <tr
                      key={transaction.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => setSelectedTransaction(transaction)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {getStatusIcon(transaction.status)}
                          <span className={`ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusClass(transaction.status)}`}>
                            {transaction.status}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">{transaction.type}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">{transaction.location_id}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">
                          {formatDate(transaction.created_at)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">
                          {formatDate(transaction.processed_at)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        {/* Transaction Details */}
        <div className="lg:col-span-1">
          <Card className="p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Transaction Details</h3>
            {selectedTransaction ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">ID</label>
                  <p className="mt-1 text-sm text-gray-900 font-mono">{selectedTransaction.id}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <div className="mt-1 flex items-center">
                    {getStatusIcon(selectedTransaction.status)}
                    <span className={`ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusClass(selectedTransaction.status)}`}>
                      {selectedTransaction.status}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Type</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedTransaction.type}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Location ID</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedTransaction.location_id}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Created At</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {formatDate(selectedTransaction.created_at)}
                  </p>
                </div>

                {selectedTransaction.processed_at && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Processed At</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {formatDate(selectedTransaction.processed_at)}
                    </p>
                  </div>
                )}

                {selectedTransaction.error && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Error</label>
                    <p className="mt-1 text-sm text-red-600">{selectedTransaction.error}</p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700">Payload</label>
                  <pre className="mt-1 p-4 bg-gray-50 rounded-md overflow-auto text-xs">
                    {JSON.stringify(selectedTransaction.payload, null, 2)}
                  </pre>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500">Select a transaction to view details</p>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}